import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Eye, Loader2 } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Switch } from 'components/ui/switch';
import { Textarea } from 'components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'components/ui/collapsible';
import { blogGenPreview, blogGenUpdateOutline, blogPromptsLint } from '../../../services/api';
import { errMsg, useDebouncedCallback } from './util';

const DATA_CARDS = [
  { key: 'confirmed_players', label: 'Confirmed players', hint: 'The only names the writer may use' },
  { key: 'player_stats', label: 'Player stats', hint: 'Ownership, form, fixtures, xPts' },
  { key: 'presenter_notes', label: 'Presenter notes', hint: 'What the writer writes from' },
  { key: 'author_notes', label: 'Your notes', hint: 'What to emphasise' },
];

export default function StageOutline({ post, blogTypes, onSaved, templateOverride, setTemplateOverride, isAdmin, onJumpTs }) {
  const ext = post.extraction || {};
  const [sections, setSections] = useState(ext.sections || []);
  const [authorNotes, setAuthorNotes] = useState(post.author_notes || '');
  const [title, setTitle] = useState(post.title || '');
  const [blogTypeId, setBlogTypeId] = useState(post.blog_type_id ? String(post.blog_type_id) : '');
  const [targetGw, setTargetGw] = useState(post.target_gw ?? '');
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(null); // key | 'full'
  const [lint, setLint] = useState(null);
  const [advOpen, setAdvOpen] = useState(false);

  useEffect(() => {
    setSections(post.extraction?.sections || []);
    setAuthorNotes(post.author_notes || '');
    setTitle(post.title || '');
    setBlogTypeId(post.blog_type_id ? String(post.blog_type_id) : '');
    setTargetGw(post.target_gw ?? '');
  }, [post.id, post.generation_run_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (patch) => {
    setSaving(true);
    try {
      const st = await blogGenUpdateOutline({ post_id: post.id, ...patch });
      onSaved(st);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };
  const debouncedSave = useDebouncedCallback(save, 800);

  const patchSection = (i, patch) => {
    const next = sections.map((s, j) => (j === i ? { ...s, ...patch } : s));
    setSections(next);
    debouncedSave({ sections: next });
  };

  const loadPreview = async (key) => {
    setPreviewOpen(key);
    if (!preview) {
      try {
        setPreview(await blogGenPreview(post.id, 'write_blog', templateOverride || null));
      } catch (e) {
        toast.error(errMsg(e));
      }
    }
  };

  const lintOverride = useDebouncedCallback(async (content) => {
    if (!content) return setLint(null);
    try {
      setLint(await blogPromptsLint('write_blog', content, post.id));
    } catch (e) {
      setLint({ lint: { errors: [errMsg(e)], warnings: [] } });
    }
  }, 700);

  const loadDefaultTemplate = async () => {
    try {
      const pv = await blogGenPreview(post.id, 'write_blog');
      setTemplateOverride(pv.user_template);
      setPreview(pv);
      setLint({ lint: pv.lint, rendered: pv.rendered_user });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="sm:col-span-3">
          <Label>Working title</Label>
          <Input value={title} onChange={(e) => { setTitle(e.target.value); debouncedSave({ title: e.target.value }); }} className="h-9" />
        </div>
        <div>
          <Label>Blog type</Label>
          <Select value={blogTypeId || '__none'} onValueChange={(v) => { const id = v === '__none' ? null : parseInt(v, 10); setBlogTypeId(v === '__none' ? '' : v); setPreview(null); save({ blog_type_id: id }); }}>
            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none">General post</SelectItem>
              {(blogTypes || []).map((t) => <SelectItem key={t.id} value={String(t.id)}>{t.name} · stats {t.stats_mode}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Target GW (changing it refreshes stats)</Label>
          <Input type="number" min={0} max={38} value={targetGw} className="h-9"
            onChange={(e) => setTargetGw(e.target.value)}
            onBlur={() => targetGw !== '' && parseInt(targetGw, 10) !== post.target_gw && (setPreview(null), save({ target_gw: parseInt(targetGw, 10) }))} />
        </div>
        <div className="flex items-end text-token-base text-muted-foreground">{saving ? <><Loader2 size={14} className="mr-1 animate-spin" /> Saving…</> : 'Saved'}</div>
      </div>

      <div>
        <Label>Sections (from the video — edit the notes the writer will use)</Label>
        <div className="mt-1 space-y-2">
          {sections.map((s, i) => (
            <div key={i} className={`rounded-md border border-borderGrey p-3 ${s.include === false || s.off_topic ? 'opacity-60' : ''}`}>
              <div className="mb-2 flex items-center gap-2">
                <Switch checked={s.include !== false && !s.off_topic} onCheckedChange={(v) => patchSection(i, { include: v, off_topic: v ? false : s.off_topic })} />
                <Input value={s.heading} onChange={(e) => patchSection(i, { heading: e.target.value })} className="h-8 flex-1" />
                {s.off_topic && <Badge variant="secondary">off-topic</Badge>}
                {s.start_timestamp && (
                  <button type="button" className="text-[11px] text-primary underline" onClick={() => onJumpTs(s.start_timestamp)}>{s.start_timestamp}</button>
                )}
              </div>
              <Textarea rows={3} value={s.notes} onChange={(e) => patchSection(i, { notes: e.target.value })} className="text-token-base" />
              {s.players_mentioned?.length > 0 && (
                <p className="mt-1 text-[11px] text-muted-foreground">Players: {s.players_mentioned.join(', ')}</p>
              )}
            </div>
          ))}
        </div>
      </div>

      <div>
        <Label>What to emphasise (optional — goes to the writer as “Notes for this post”)</Label>
        <Textarea rows={3} value={authorNotes} onChange={(e) => { setAuthorNotes(e.target.value); setPreview(null); debouncedSave({ author_notes: e.target.value }); }} placeholder="e.g. Lead with the Solanke injury. Keep it under 900 words." />
      </div>

      <div>
        <Label>Data inserted into the prompt</Label>
        <div className="mt-1 grid grid-cols-2 gap-2 lg:grid-cols-4">
          {DATA_CARDS.map((c) => (
            <button key={c.key} type="button" onClick={() => loadPreview(c.key)} className="rounded-md border border-borderGrey p-3 text-left hover:bg-muted/40">
              <div className="flex items-center justify-between text-token-base font-medium">{c.label} <Eye size={14} /></div>
              <div className="text-[11px] text-muted-foreground">{c.hint}</div>
            </button>
          ))}
        </div>
        <Button variant="link" size="sm" className="px-0" onClick={() => loadPreview('full')}>View the full prompt the writer will receive</Button>
      </div>

      {isAdmin && (
        <Collapsible open={advOpen} onOpenChange={setAdvOpen}>
          <CollapsibleTrigger className="text-token-base text-primary underline">Advanced: override the write template for this post only</CollapsibleTrigger>
          <CollapsibleContent className="mt-2 space-y-2">
            {!templateOverride && <Button size="sm" variant="outline" onClick={loadDefaultTemplate}>Load the current template</Button>}
            {templateOverride !== '' && templateOverride != null && (
              <>
                <Textarea rows={14} value={templateOverride} className="font-mono text-[12px]"
                  onChange={(e) => { setTemplateOverride(e.target.value); lintOverride(e.target.value); }} />
                {lint?.lint?.errors?.length > 0 && <div className="text-token-base text-destructive">{lint.lint.errors.map((e, i) => <div key={i}>• {e}</div>)}</div>}
                {lint?.lint?.warnings?.length > 0 && <div className="text-token-base text-warning">{lint.lint.warnings.map((e, i) => <div key={i}>• {e}</div>)}</div>}
                <Button size="sm" variant="ghost" onClick={() => { setTemplateOverride(null); setLint(null); }}>Discard override</Button>
              </>
            )}
          </CollapsibleContent>
        </Collapsible>
      )}

      <Dialog open={!!previewOpen} onOpenChange={(o) => !o && setPreviewOpen(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[800px]">
          <DialogHeader>
            <DialogTitle>{previewOpen === 'full' ? 'Full prompt (Pass 2)' : DATA_CARDS.find((c) => c.key === previewOpen)?.label}</DialogTitle>
          </DialogHeader>
          {!preview ? <p className="text-muted-foreground">Loading…</p> : (
            <>
              <p className="text-token-base text-muted-foreground">
                Template: {preview.source} · model {preview.model} · effort {preview.effort} · ≈ {preview.estimated_input_tokens} input tokens
                {preview.lint?.errors?.length ? ` · ${preview.lint.errors.length} lint errors` : ''}
              </p>
              <pre className="whitespace-pre-wrap rounded-md bg-muted/40 p-3 font-mono text-[12px]">
                {previewOpen === 'full' ? preview.rendered_user : (preview.values?.[previewOpen] ?? '')}
              </pre>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
